import { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import api from '@/utils/api';
import { uploadImageToS3 } from '@/utils/s3Upload';
import { toast } from 'sonner';
import { PenTool, Upload, Trash2, Loader2 } from 'lucide-react';

export const PlatformSignatureCard = () => {
  const [signature, setSignature] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const res = await api.get('/admin/platform-settings/signature');
      setSignature(res.data);
    } catch (e) {
      console.error('Failed to load signature', e);
    }
  };

  useEffect(() => { load(); }, []);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const { url, file_key } = await uploadImageToS3(file, 'signatures/').promise;
      await api.put('/admin/platform-settings/signature', { signature_file_url: url, signature_file_key: file_key });
      toast.success('Signature saved');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to upload signature');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      await api.delete('/admin/platform-settings/signature');
      toast.success('Signature removed');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove signature');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mt-6" style={{ borderColor: '#E3E1DB' }} data-testid="platform-signature-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
          <PenTool className="h-5 w-5" /> Platform Signature
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm" style={{ color: '#5C635A' }}>
          Admin-only. This signature is stamped on every Letter of Model Acquisition issued to learners. PNG with transparent background recommended.
        </p>
        {signature?.has_signature ? (
          <div className="p-4 rounded-lg border bg-white" style={{ borderColor: '#E3E1DB' }}>
            {signature.signature_presigned_url ? (
              <img src={signature.signature_presigned_url} alt="Platform signature" className="h-20 object-contain" data-testid="signature-preview" />
            ) : (
              <p className="text-sm" data-testid="signature-preview">Signature on file</p>
            )}
            {signature.signature_updated_at && (
              <p className="text-xs mt-2" style={{ color: '#5C635A' }}>
                Updated {new Date(signature.signature_updated_at).toLocaleString()}
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm italic" style={{ color: '#5C635A' }} data-testid="no-signature-msg">No signature uploaded yet.</p>
        )}
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="signature-file-input" />
        <div className="flex gap-2">
          <Button onClick={() => inputRef.current?.click()} disabled={busy} style={{ backgroundColor: '#163329' }} data-testid="upload-signature-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            {signature?.has_signature ? 'Replace Signature' : 'Upload Signature'}
          </Button>
          {signature?.has_signature && (
            <Button variant="outline" onClick={handleDelete} disabled={busy} data-testid="delete-signature-btn">
              <Trash2 className="h-4 w-4 mr-2" /> Remove
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
